import { ApolloError } from '@apollo/client'
import { FC, useEffect, useMemo } from 'react'
import LazyHydrate from 'react-lazy-hydration'

import { reportClientError } from '../../../../app/_components/chrome/scripts/DataDogRumScript'
import { RenderOnceInViewport } from '../../../../app/_components/performance/RenderOnceInViewport.client'
import { NotFoundPage } from '../../../../app/_not-found.client'
import { TrackPageView } from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { PageTestimonialSection } from '../../../../brunswick/components/page-sections/PageTestimonialSection'
import { canonicalBaseUrls } from '../../../../constants/figs'
import { Kit, KitContent } from '../../../../types/graphql'
import { KitSection, Product, ProductAsset } from '../../../../types/graphql'
import { PageSection } from '../../../page-sections/PageSection'
import { GalleryImageStateContainer } from '../ProductPage/contexts/GalleryImageStateContainer'
import { FigsScrubsAreSection } from '../ProductPage/FigsScrubsAreSection'
import { ImageZoom } from '../ProductPage/ImageZoom'
import { SchemaTags } from '../ProductPage/SchemaTags'
import ShopTheLookSection from '../ProductPage/ShopTheLookSection'
import * as ProductPageStyles from '../styles'

import { KitDetails } from './KitDetails'
import { KitSwiper } from './KitSwiper'

export interface KitPageMainProps
  extends Readonly<{
    errors: ReadonlyArray<Error | ApolloError>
    handle: string
    kit: Kit
    kitContent: KitContent | null
  }> {}

export const KitPageMain: FC<KitPageMainProps> = ({ errors, handle, kitContent, kit }) => {
  const {
    pages: {
      product: { scrubCategories },
    },
  } = useTranslation()

  //check if this is an empty kit (no productGroups in the collection)
  const isEmptyKit = !kit.collection?.productGroups?.length
  const isUnavailableKitProduct = !kit.productGroup?.products.length

  //check that the productGroups in the collection have necessary fields
  const hasInvalidProductGroups = kit.collection?.productGroups?.some(productGroup => {
    return !productGroup.products.length
  })

  const hasErrors = errors.length > 0

  const didQueryFail: boolean =
    hasErrors || isEmptyKit || isUnavailableKitProduct || hasInvalidProductGroups

  useEffect(() => {
    if (!didQueryFail || !hasErrors) return
    reportClientError({
      error: errors?.[0],
      context: {
        scope: 'Kit Page',
        label: 'kit query failed',
      },
    })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didQueryFail, errors, handle])

  const canonicalUrl = `${canonicalBaseUrls.kits}${handle}`

  const splitColors = !(kitContent?.groupItemsByColor ?? true)

  const kitCollectionItems = useMemo(() => {
    return kit.collection.productGroups ?? []
  }, [kit.collection.productGroups])

  const kitItems = useMemo(() => {
    return splitColors
      ? kitCollectionItems.flatMap(productGroup => {
          //chunk the products array into arrays for each color, but keep different fits together
          const productsGroupedByColor = productGroup.products.reduce<Record<string, Product[]>>(
            (accumulator, product) => {
              const key: string = `${product.colorInfo?.rawName}`
              if (!accumulator[key]) accumulator[key] = []
              accumulator[key]?.push(product)
              return accumulator
            },
            {}
          )
          //convert each new color array into it's own productGroup so it can be a unique item in the kit
          return Object.values(productsGroupedByColor).map(productByColor => ({
            ...productGroup,
            colors: productGroup.colors.filter(
              colorDetail => colorDetail.rawName === productByColor[0]?.colorInfo?.rawName
            ),
            products: [...productByColor],
          }))
        })
      : kitCollectionItems
  }, [kitCollectionItems, splitColors])

  const kitProduct = kit.productGroup?.products[0]

  const firstItem = kitItems[0] ?? null

  const images = useMemo(() => {
    return kitProduct?.images?.nodes ?? []
  }, [kitProduct?.images?.nodes])

  const defaultColors = useMemo(() => {
    try {
      const parsedDefaultColors = JSON.parse(kitContent?.defaultColors || '', (key, val) =>
        typeof val === 'string' || !key ? val : ''
      )

      // make defaultColors object keys lowercased
      return Object.fromEntries(
        Object.entries(parsedDefaultColors).map(([key, value]) => [key.toLowerCase(), value])
      ) as Record<string, string>
    } catch (_e) {}

    return {}
  }, [kitContent?.defaultColors])

  const galleryImageList: ReadonlyArray<ProductAsset> = images.filter(o => o.kind !== 'wwlt') || []
  //sidebar with list of object in kit
  const kitDetailsSection = useMemo(() => {
    return !isEmptyKit ? (
      <KitDetails
        kitProductGroup={kit.productGroup}
        kitItemList={kitItems}
        defaultColors={defaultColors}
        hideIndividualProductDetailsModals={kitContent?.hideProductDetails ?? false}
        accordion={kitContent?.accordion}
      />
    ) : null
  }, [
    defaultColors,
    isEmptyKit,
    kit.productGroup,
    kitContent?.accordion,
    kitContent?.hideProductDetails,
    kitItems,
  ])

  const kitSwiperBannerSection = useMemo(() => {
    return kit ? <KitSwiper kit={kit} /> : null
  }, [kit])

  if (didQueryFail) return <NotFoundPage />

  return (
    <GalleryImageStateContainer.Provider initialState={{ images: galleryImageList }}>
      <TrackPageView pageViewHandle={kit.collection.name} pageViewCategory='kit' />
      <ProductPageStyles.Main>
        <ImageZoom />

        <ProductPageStyles.PrimaryAndAsideSection>
          <LazyHydrate whenVisible>
            <ProductPageStyles.PrimaryColumnDiv>
              {kitSwiperBannerSection}
            </ProductPageStyles.PrimaryColumnDiv>
          </LazyHydrate>

          <LazyHydrate whenVisible>
            <ProductPageStyles.AsideColumnDiv>{kitDetailsSection}</ProductPageStyles.AsideColumnDiv>
          </LazyHydrate>
        </ProductPageStyles.PrimaryAndAsideSection>

        {kitContent?.sections.length ? (
          <KitPageContentfulSections {...kitContent.sections} />
        ) : null}

        {kitContent?.testimonials.length ? (
          <section>
            <RenderOnceInViewport offset={{ bottom: 200 }}>
              <PageTestimonialSection
                __typename='PageTestimonialSection'
                id='kitContent'
                title={kitContent.testimonialsTitle || ''}
                subtitle={kitContent.testimonialsSubtitle || ''}
                backgroundColor='#f3f3f3'
                testimonials={kitContent.testimonials || []}
              />
            </RenderOnceInViewport>
          </section>
        ) : null}

        <section>
          <RenderOnceInViewport offset={{ bottom: 200 }}>
            <ShopTheLookSection productHandle={handle} isKit />
          </RenderOnceInViewport>
        </section>

        {scrubCategories.includes(firstItem?.category || '') ? (
          <section>
            <RenderOnceInViewport offset={{ bottom: 200 }}>
              <FigsScrubsAreSection />
            </RenderOnceInViewport>
          </section>
        ) : null}

        {kit.productGroup && canonicalUrl ? (
          <LazyHydrate whenIdle>
            <SchemaTags
              productGroup={kit.productGroup}
              canonicalUrl={canonicalUrl}
              product={kitProduct}
            />
          </LazyHydrate>
        ) : null}
      </ProductPageStyles.Main>
    </GalleryImageStateContainer.Provider>
  )
}

const KitPageContentfulSections: FC<ReadonlyArray<KitSection>> = sections => {
  // create valid array from readonly array
  const contentfulSections: KitSection[] = []
  for (const key in sections) {
    const section = sections[key]
    if (section) contentfulSections.push(section)
  }

  if (contentfulSections.length < 1) return null

  return (
    <>
      {contentfulSections.map((section, index) => {
        const key = `${section.id}-${index}`
        return (
          <ProductPageStyles.Section key={key}>
            <RenderOnceInViewport offset={{ bottom: 200 }}>
              <PageSection key={key} sectionModel={section} index={index + 1} />
            </RenderOnceInViewport>
          </ProductPageStyles.Section>
        )
      })}
    </>
  )
}

export default KitPageMain
